import { IPivotItemProps, useTheme } from '@fluentui/react';
import { IPPivot, IPText } from 'baseComponents';
import { IPPivotItem } from 'baseComponents/Pivot/IPPivotItem';
import { DialogOk } from 'components/Dialogs/DialogOk';
import { globalPaddingScene, globalTextStylesBold } from 'globalStyles';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { GetCurrentURL, getCurrentURLWithoutLastPart, getSearchWithParams } from 'utils/url';

export interface IScenePivotItemProps {
  className?: string;
  disabled?: boolean;
  disabledReason?: string;
  key: string;
  text: string;
  url: string;
}

export interface IScenePivotProps {
  items: IScenePivotItemProps[];
  selectedKey: string | undefined;
  onSelectPivot?: (item: IScenePivotItemProps) => void;
  //query parameters of the current URL that survive a pivot click, e.g. frameworkView on a detail scene
  preserveQueryParams?: string[];
}

export const getCurrentPivotKeyFromURL = (pivotItems: IScenePivotItemProps[]): string => {
  const url = GetCurrentURL(true);

  const pi = pivotItems.find((i) => i.url === url);
  if (pi) {
    return pi.key;
  } else {
    return '';
  }
};

const ScenePivot = (props: IScenePivotProps) => {
  const history = useHistory();
  const theme = useTheme();
  const [disabledPivot, setDisabledPivot] = useState<IScenePivotItemProps | undefined>(undefined);

  const handleLinkClick = (item: IPPivotItem | undefined) => {
    if (item && item.props.itemKey) {
      const pivot = props.items.find((i) => i.key === item.props.itemKey);
      if (pivot?.disabled) {
        setDisabledPivot(pivot);

        return;
      }

      if (pivot) {
        if (props.onSelectPivot) props.onSelectPivot(pivot);
        const search = props.preserveQueryParams
          ? getSearchWithParams(window.location.search, props.preserveQueryParams)
          : '';
        const url: string = getCurrentURLWithoutLastPart() + pivot.url + search;
        history.push(url);
      }
    }
  };

  //remove the bold view when no pivot item is selected
  const renderItem = (
    itemProps?: IPivotItemProps,
    defaultRender?: (itemProps?: IPivotItemProps) => JSX.Element | null,
  ): JSX.Element | null => {
    const pivot = props.items.find((item) => item.key === itemProps?.itemKey);

    return (
      <IPText
        styles={{
          ...(itemProps?.itemKey === props.selectedKey ? globalTextStylesBold : {}),
          ...(pivot?.disabled ? { root: { color: theme.semanticColors.disabledText } } : {}),
        }}
      >
        {itemProps?.headerText}
      </IPText>
    );
  };

  //remove the blue underline when no pivot item is selected
  const getPivotStyles = () => {
    return !props.selectedKey
      ? {
          root: {
            paddingLeft: globalPaddingScene,
          },
          linkIsSelected: {
            selectors: {
              '&:before': { height: 0 },
            },
          },
        }
      : {
          root: {
            paddingLeft: globalPaddingScene,
          },
        };
  };

  return (
    <>
      <IPPivot
        onLinkClick={handleLinkClick}
        headersOnly={true}
        selectedKey={props.selectedKey}
        styles={getPivotStyles()}
        overflowBehavior="menu"
      >
        {props.items.map((pivot) => {
          return (
            <IPPivotItem
              headerButtonProps={{
                ...(pivot.className ? { 'data-redlab': pivot.className } : {}),
                'aria-disabled': pivot.disabled,
                title: pivot.disabledReason,
              }}
              key={pivot.key}
              headerText={pivot.text}
              itemKey={pivot.key}
              onRenderItemLink={renderItem}
            />
          );
        })}
      </IPPivot>
      <DialogOk
        hidden={!disabledPivot}
        onOk={() => setDisabledPivot(undefined)}
        title={disabledPivot?.text ?? ''}
        subText={disabledPivot?.disabledReason ?? ''}
      />
    </>
  );
};

export default ScenePivot;
